/**
 * WishingCardComposer — modal sheet to send a culturally-appropriate
 * greeting card to family/friends in 1 tap.
 *
 * Flow:
 *   1. (Optional) Pre-selected occasion from caller (Friday → Jumu'ah,
 *      Eid date detected → Eid Mubarak, etc.)
 *   2. Pick recipient name (manual entry; future iteration will pull from
 *      contacts/family with permission priming)
 *   3. Edit the bilingual greeting message (sender name + verse + occasion)
 *   4. Pick channel → WhatsApp (wa.me link) / native share / copy
 *
 * Occasions cover Muslim conventions (Eid, Jumu'ah, Mabrouk, Hajj, etc.)
 * + universal (Birthday, Anniversary, Get well, Condolences).
 *
 * BOUNDARY (see docs/yawmi-vs-memoriz-boundary.md):
 *   - The compose UI + share flow + occasion CATALOG = Yawmi-domain
 *     (this file is Yawmi-scoped, lives under modules/home until the
 *     standalone Yawmi app ships).
 *   - The Arabic verses, hadith, duas baked into the templates BELOW =
 *     **Memoriz-domain CONTENT**. Currently hardcoded as v1 because
 *     Memoriz hasn't yet exposed `getQuoteForOccasion()` API. When that
 *     ships, all Arabic religious text moves out of this file → Yawmi
 *     fetches `Memoriz.getQuoteForOccasion(occasionId, lang)` and uses
 *     the returned text. See TODO marker on the OCCASIONS array.
 *
 * Privacy: nothing leaves the device until the user explicitly hits Share.
 * No contacts API access in this v1; user types the recipient's name.
 */

import type { AweniContext } from '../../context/AweniContext';

export type WishingOccasionId =
  | 'jumuah'
  | 'eid_fitr'
  | 'eid_adha'
  | 'ramadan'
  | 'mabrouk_wedding'
  | 'mabrouk_baby'
  | 'hajj_mubarak'
  | 'birthday'
  | 'anniversary'
  | 'get_well'
  | 'condolences'
  | 'thank_you'
  | 'congratulations';

export interface WishingOccasion {
  id: WishingOccasionId;
  emoji: string;
  /** Localized labels (occasion title) per locale. */
  labels: { en: string; fr: string; ar: string; es: string; de: string };
  /** Pre-filled greeting body with {{name}} and {{sender}} placeholders. */
  templates: { en: string; fr: string; ar: string; es: string; de: string };
  /** Background gradient for the card visual + CTA pill. */
  gradient: string;
  /** Tone — affects design (somber for condolences, no animations). */
  tone: 'celebratory' | 'spiritual' | 'somber' | 'warm';
}

// TODO(memoriz-extraction): Once Memoriz exposes `GET /memoriz/api/v1/quotes
// ?occasion=<id>&lang=<locale>`, fetch the Arabic religious content (verses,
// hadith, duas) from there instead of hardcoding here. Yawmi keeps the
// occasion CATALOG (id, emoji, gradient, tone, EN labels) but the religious
// Arabic content is Memoriz-owned. Migration plan: replace each `templates`
// entry with `{{islamic_quote}}` placeholder + a runtime fetch in compose.
const OCCASIONS: WishingOccasion[] = [
  {
    id: 'jumuah', emoji: '🕌',
    labels: { en: "Jumu'ah Mubarak", fr: "Jumu'ah Mubarak", ar: 'جمعة مباركة', es: "Jumu'ah Mubarak", de: "Jumu'ah Mubarak" },
    templates: {
      en: "{{name}}, Jumu'ah Mubarak 🤲\n\n\"The best day on which the sun has risen is Friday\" — Sahih Muslim\n\nMay this Friday bring you peace, blessings, and answered duas.\n\n— {{sender}}",
      fr: "{{name}}, Jumu'ah Mubarak 🤲\n\n« Le meilleur jour où le soleil se lève est le vendredi » — Sahih Muslim\n\nQu'Allah t'accorde paix, bénédictions et que tes invocations soient exaucées.\n\n— {{sender}}",
      ar: "{{name}}، جمعة مباركة 🤲\n\n«خير يوم طلعت عليه الشمس يوم الجمعة» — صحيح مسلم\n\nأسأل الله أن يبارك لك في يومك ويستجيب دعاءك.\n\n— {{sender}}",
      es: "{{name}}, Jumu'ah Mubarak 🤲\n\n\"El mejor día sobre el que ha salido el sol es el viernes\" — Sahih Muslim\n\nQue este viernes te traiga paz, bendiciones y duas aceptadas.\n\n— {{sender}}",
      de: "{{name}}, Jumu'ah Mubarak 🤲\n\n„Der beste Tag, an dem die Sonne aufgegangen ist, ist der Freitag\" — Sahih Muslim\n\nMöge dieser Freitag dir Frieden, Segen und erhörte Bittgebete bringen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#0A0A0A,#2A2520)', tone: 'spiritual',
  },
  {
    id: 'eid_fitr', emoji: '🌙',
    labels: { en: 'Eid al-Fitr Mubarak', fr: 'Aïd el-Fitr Moubarak', ar: 'عيد فطر مبارك', es: 'Eid al-Fitr Mubarak', de: 'Eid al-Fitr Mubarak' },
    templates: {
      en: "{{name}}, Eid Mubarak! 🌙✨\n\nMay Allah accept your fasts, prayers, and good deeds. May this Eid bring joy to you and your family.\n\nKul 'aam wa antum bi-khair.\n\n— {{sender}}",
      fr: "{{name}}, Aïd Moubarak ! 🌙✨\n\nQu'Allah accepte ton jeûne, tes prières et tes bonnes actions. Que cet Aïd t'apporte joie et bonheur à toi et ta famille.\n\nKul 'aam wa antum bi-khair.\n\n— {{sender}}",
      ar: "{{name}}، عيد مبارك 🌙✨\n\nتقبل الله صيامك وقيامك وصالح أعمالك. كل عام وأنتم بخير.\n\n— {{sender}}",
      es: "{{name}}, Eid Mubarak! 🌙✨\n\nQue Allah acepte tu ayuno, oraciones y buenas obras. Que este Eid traiga alegría a ti y a tu familia.\n\n— {{sender}}",
      de: "{{name}}, Eid Mubarak! 🌙✨\n\nMöge Allah dein Fasten, deine Gebete und guten Taten annehmen. Möge dieses Eid dir und deiner Familie Freude bringen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)', tone: 'celebratory',
  },
  {
    id: 'eid_adha', emoji: '🐑',
    labels: { en: 'Eid al-Adha Mubarak', fr: 'Aïd el-Adha Moubarak', ar: 'عيد أضحى مبارك', es: 'Eid al-Adha Mubarak', de: 'Eid al-Adha Mubarak' },
    templates: {
      en: "{{name}}, Eid al-Adha Mubarak 🐑\n\nMay Allah accept your sacrifice and reward you abundantly. Wishing you and your loved ones a blessed Eid.\n\n— {{sender}}",
      fr: "{{name}}, Aïd el-Adha Moubarak 🐑\n\nQu'Allah accepte ton sacrifice et te récompense abondamment. Bel Aïd à toi et tes proches.\n\n— {{sender}}",
      ar: "{{name}}، عيد أضحى مبارك 🐑\n\nتقبل الله أضحيتك وضاعف لك الأجر. كل عام وأنتم بخير.\n\n— {{sender}}",
      es: "{{name}}, Eid al-Adha Mubarak 🐑\n\nQue Allah acepte tu sacrificio y te recompense abundantemente. Un Eid bendecido para ti y tus seres queridos.\n\n— {{sender}}",
      de: "{{name}}, Eid al-Adha Mubarak 🐑\n\nMöge Allah dein Opfer annehmen und dich reichlich belohnen. Ein gesegnetes Eid dir und deinen Lieben.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#8B5CF6,#EC4899)', tone: 'celebratory',
  },
  {
    id: 'ramadan', emoji: '🌙',
    labels: { en: 'Ramadan Kareem', fr: 'Ramadan Karim', ar: 'رمضان كريم', es: 'Ramadán Karim', de: 'Ramadan Kareem' },
    templates: {
      en: "{{name}}, Ramadan Kareem 🌙\n\nMay this blessed month be a source of peace, mercy, and forgiveness for you and your family.\n\n— {{sender}}",
      fr: "{{name}}, Ramadan Karim 🌙\n\nQue ce mois béni soit pour toi et ta famille une source de paix, de miséricorde et de pardon.\n\n— {{sender}}",
      ar: "{{name}}، رمضان كريم 🌙\n\nأسأل الله أن يجعله شهر سلام ورحمة ومغفرة لك ولأهلك.\n\n— {{sender}}",
      es: "{{name}}, Ramadán Karim 🌙\n\nQue este mes bendito sea fuente de paz, misericordia y perdón para ti y tu familia.\n\n— {{sender}}",
      de: "{{name}}, Ramadan Kareem 🌙\n\nMöge dieser gesegnete Monat dir und deiner Familie Frieden, Barmherzigkeit und Vergebung bringen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#0EA5E9,#6366F1)', tone: 'spiritual',
  },
  {
    id: 'mabrouk_wedding', emoji: '💐',
    labels: { en: 'Mabrouk — Wedding', fr: 'Mabrouk — Mariage', ar: 'مبروك الزواج', es: 'Mabrouk — Boda', de: 'Mabrouk — Hochzeit' },
    templates: {
      en: "{{name}}, mabrouk! 💐\n\nMay Allah bless your union and grant you both love, mercy, and a beautiful life together.\n\n— {{sender}}",
      fr: "{{name}}, mabrouk ! 💐\n\nQu'Allah bénisse ton union et vous accorde amour, miséricorde et une belle vie ensemble.\n\n— {{sender}}",
      ar: "{{name}}، مبروك الزواج 💐\n\nبارك الله لكما وبارك عليكما وجمع بينكما في خير.\n\n— {{sender}}",
      es: "{{name}}, mabrouk! 💐\n\nQue Allah bendiga su unión y les conceda amor, misericordia y una hermosa vida juntos.\n\n— {{sender}}",
      de: "{{name}}, mabrouk! 💐\n\nMöge Allah eure Verbindung segnen und euch Liebe, Barmherzigkeit und ein schönes gemeinsames Leben schenken.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#EC4899,#F59E0B)', tone: 'celebratory',
  },
  {
    id: 'mabrouk_baby', emoji: '👶',
    labels: { en: 'Mabrouk — Newborn', fr: 'Mabrouk — Naissance', ar: 'مبروك المولود', es: 'Mabrouk — Recién nacido', de: 'Mabrouk — Neugeborenes' },
    templates: {
      en: "{{name}}, mabrouk on the new arrival! 👶\n\nBārakallāhu lakum fī l-mawhūbi lakum — may Allah bless you in what He has gifted you. May the little one grow in faith and love.\n\n— {{sender}}",
      fr: "{{name}}, mabrouk pour cette nouvelle arrivée ! 👶\n\nQu'Allah te bénisse dans ce don. Que ce petit ange grandisse dans la foi et l'amour.\n\n— {{sender}}",
      ar: "{{name}}، مبروك المولود 👶\n\nبارك الله لكم في الموهوب وشكرتم الواهب وبلغ أشده ورزقتم بره.\n\n— {{sender}}",
      es: "{{name}}, mabrouk por el recién nacido! 👶\n\nQue Allah te bendiga en este regalo. Que el pequeño crezca en fe y amor.\n\n— {{sender}}",
      de: "{{name}}, mabrouk zur Geburt! 👶\n\nMöge Allah dich in diesem Geschenk segnen. Möge das Kleine in Glauben und Liebe aufwachsen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#0EA5E9,#0A0A0A)', tone: 'celebratory',
  },
  {
    id: 'hajj_mubarak', emoji: '🕋',
    labels: { en: 'Hajj Mubarak', fr: 'Hadj Moubarak', ar: 'حج مبرور', es: 'Hajj Mubarak', de: 'Hajj Mubarak' },
    templates: {
      en: "{{name}}, Hajj Mubarak 🕋\n\nMay Allah accept your pilgrimage and grant you a hajj mabrūr — one whose only reward is paradise.\n\n— {{sender}}",
      fr: "{{name}}, Hadj Moubarak 🕋\n\nQu'Allah accepte ton pèlerinage et t'accorde un hadj mabroûr — dont la seule récompense est le paradis.\n\n— {{sender}}",
      ar: "{{name}}، حج مبرور وذنب مغفور 🕋\n\nتقبل الله منك الحج والسعي وجعله حجاً مبروراً وذنباً مغفوراً.\n\n— {{sender}}",
      es: "{{name}}, Hajj Mubarak 🕋\n\nQue Allah acepte tu peregrinación y te conceda un hajj mabrur — cuya única recompensa es el paraíso.\n\n— {{sender}}",
      de: "{{name}}, Hajj Mubarak 🕋\n\nMöge Allah deine Pilgerfahrt annehmen und dir einen Hajj Mabrur gewähren — dessen einziger Lohn das Paradies ist.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#F59E0B,#0A0A0A)', tone: 'spiritual',
  },
  {
    id: 'birthday', emoji: '🎉',
    labels: { en: 'Happy Birthday', fr: 'Joyeux anniversaire', ar: 'كل سنة وأنت طيب', es: 'Feliz cumpleaños', de: 'Alles Gute zum Geburtstag' },
    templates: {
      en: "{{name}}, happy birthday 🎉\n\nMay Allah grant you another year of health, faith, and joy. Kul 'aam wa anta bi-khair.\n\n— {{sender}}",
      fr: "{{name}}, joyeux anniversaire 🎉\n\nQu'Allah t'accorde une nouvelle année de santé, de foi et de bonheur. Kul 'aam wa anta bi-khair.\n\n— {{sender}}",
      ar: "{{name}}، كل عام وأنت بخير 🎉\n\nأسأل الله أن يبارك لك في عمرك ويرزقك صحة وإيماناً وسروراً.\n\n— {{sender}}",
      es: "{{name}}, feliz cumpleaños 🎉\n\nQue Allah te conceda otro año de salud, fe y alegría.\n\n— {{sender}}",
      de: "{{name}}, alles Gute zum Geburtstag 🎉\n\nMöge Allah dir ein weiteres Jahr voller Gesundheit, Glauben und Freude schenken.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#EC4899,#F59E0B)', tone: 'warm',
  },
  {
    id: 'anniversary', emoji: '💞',
    labels: { en: 'Happy Anniversary', fr: "Joyeux anniversaire de mariage", ar: 'ذكرى زواج سعيدة', es: 'Feliz aniversario', de: 'Frohen Hochzeitstag' },
    templates: {
      en: "{{name}}, happy anniversary 💞\n\nMay Allah continue to bless your union with love, mercy, and barakah.\n\n— {{sender}}",
      fr: "{{name}}, joyeux anniversaire de mariage 💞\n\nQu'Allah continue de bénir votre union d'amour, de miséricorde et de baraka.\n\n— {{sender}}",
      ar: "{{name}}، ذكرى زواج سعيدة 💞\n\nبارك الله لكما وأدام عليكما المحبة والرحمة.\n\n— {{sender}}",
      es: "{{name}}, feliz aniversario 💞\n\nQue Allah siga bendiciendo su unión con amor, misericordia y barakah.\n\n— {{sender}}",
      de: "{{name}}, frohen Hochzeitstag 💞\n\nMöge Allah eure Verbindung weiterhin mit Liebe, Barmherzigkeit und Baraka segnen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#EC4899,#8B5CF6)', tone: 'warm',
  },
  {
    id: 'get_well', emoji: '🤲',
    labels: { en: 'Get Well Soon', fr: 'Bon rétablissement', ar: 'شفاء عاجل', es: 'Pronta recuperación', de: 'Gute Besserung' },
    templates: {
      en: "{{name}}, may Allah grant you shifā' 🤲\n\nA full and quick recovery. We are praying for you.\n\n— {{sender}}",
      fr: "{{name}}, qu'Allah t'accorde la guérison 🤲\n\nUn rétablissement complet et rapide. Nous prions pour toi.\n\n— {{sender}}",
      ar: "{{name}}، شفاك الله شفاءً لا يغادر سقماً 🤲\n\nأسأل الله العلي العظيم رب العرش العظيم أن يشفيك.\n\n— {{sender}}",
      es: "{{name}}, que Allah te conceda shifā' 🤲\n\nUna recuperación completa y rápida. Estamos rezando por ti.\n\n— {{sender}}",
      de: "{{name}}, möge Allah dir Shifa' schenken 🤲\n\nEine vollständige und schnelle Genesung. Wir beten für dich.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#0A0A0A,#0EA5E9)', tone: 'warm',
  },
  {
    id: 'condolences', emoji: '🤲',
    labels: { en: 'Condolences', fr: 'Condoléances', ar: 'تعزية', es: 'Condolencias', de: 'Beileid' },
    templates: {
      en: "{{name}},\n\nInnā lillāhi wa innā ilayhi rāji'ūn. May Allah grant your loved one His mercy and forgiveness, and patience to all who are left behind.\n\n— {{sender}}",
      fr: "{{name}},\n\nInnā lillāhi wa innā ilayhi rāji'ūn. Qu'Allah accorde à ton proche Sa miséricorde et Son pardon, et patience à tous ceux qui restent.\n\n— {{sender}}",
      ar: "{{name}}،\n\nإنا لله وإنا إليه راجعون. أسأل الله أن يرحم فقيدكم ويغفر له ويلهمكم الصبر والسلوان.\n\n— {{sender}}",
      es: "{{name}},\n\nInnā lillāhi wa innā ilayhi rāji'ūn. Que Allah conceda a tu ser querido Su misericordia y perdón, y paciencia a todos los que quedan.\n\n— {{sender}}",
      de: "{{name}},\n\nInnā lillāhi wa innā ilayhi rāji'ūn. Möge Allah deinem geliebten Menschen Seine Barmherzigkeit und Vergebung schenken, und Geduld allen, die zurückbleiben.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#475569,#1E293B)', tone: 'somber',
  },
  {
    id: 'thank_you', emoji: '🙏',
    labels: { en: 'Thank You', fr: 'Merci', ar: 'شكراً', es: 'Gracias', de: 'Danke' },
    templates: {
      en: "{{name}},\n\nJazākallāhu khayran. Thank you, may Allah reward you with goodness.\n\n— {{sender}}",
      fr: "{{name}},\n\nJazākallāhu khayran. Merci, qu'Allah te récompense en bien.\n\n— {{sender}}",
      ar: "{{name}}،\n\nجزاك الله خيراً وبارك فيك.\n\n— {{sender}}",
      es: "{{name}},\n\nJazākallāhu khayran. Gracias, que Allah te recompense con bondad.\n\n— {{sender}}",
      de: "{{name}},\n\nJazākallāhu khayran. Danke, möge Allah dich mit Gutem belohnen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#0A0A0A,#FAF7F2)', tone: 'warm',
  },
  {
    id: 'congratulations', emoji: '🎊',
    labels: { en: 'Congratulations', fr: 'Félicitations', ar: 'تهانينا', es: 'Felicidades', de: 'Glückwunsch' },
    templates: {
      en: "{{name}}, mabrouk! 🎊\n\nWhat wonderful news. May Allah continue to bless your path.\n\n— {{sender}}",
      fr: "{{name}}, mabrouk ! 🎊\n\nQuelle merveilleuse nouvelle. Qu'Allah continue de bénir ton chemin.\n\n— {{sender}}",
      ar: "{{name}}، مبروك 🎊\n\nخبر جميل، بارك الله لك في كل ما تخطو إليه.\n\n— {{sender}}",
      es: "{{name}}, mabrouk! 🎊\n\nQué maravillosa noticia. Que Allah siga bendiciendo tu camino.\n\n— {{sender}}",
      de: "{{name}}, mabrouk! 🎊\n\nWelch wunderbare Nachricht. Möge Allah deinen Weg weiterhin segnen.\n\n— {{sender}}",
    },
    gradient: 'linear-gradient(135deg,#F59E0B,#EC4899)', tone: 'celebratory',
  },
];

export function getOccasions(): WishingOccasion[] {
  return OCCASIONS;
}

export function getOccasionById(id: WishingOccasionId): WishingOccasion | undefined {
  return OCCASIONS.find((o) => o.id === id);
}

export interface ComposerOptions {
  ctx: AweniContext;
  /** Pre-selected occasion (e.g., from YawmiWidget Friday detection). */
  preselect?: WishingOccasionId;
}

function localized(o: WishingOccasion, locale: string): { label: string; template: string } {
  const lang = (locale || 'en').slice(0, 2) as keyof WishingOccasion['labels'];
  return {
    label: o.labels[lang] || o.labels.en,
    template: o.templates[lang] || o.templates.en,
  };
}

function injectStyles(): void {
  if (document.getElementById('aweni-wish-composer-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-wish-composer-styles';
  style.textContent = `
    .aweni-wish-overlay {
      position: fixed; inset: 0; z-index: 9200;
      background: rgba(15,23,42,0.7);
      display: flex; align-items: flex-end; justify-content: center;
    }
    @media (min-width: 768px) { .aweni-wish-overlay { align-items: center; } }
    .aweni-wish-sheet {
      width: 100%; max-width: 480px;
      max-height: 92vh; overflow-y: auto;
      background: var(--card-bg);
      color: var(--text-primary);
      border-radius: 24px 24px 0 0;
      padding: 18px 16px 20px;
    }
    @media (min-width: 768px) { .aweni-wish-sheet { border-radius: 24px; } }
    .aweni-wish-header {
      display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
    }
    .aweni-wish-title { flex: 1; font-size: 18px; font-weight: 700; }
    .aweni-wish-close {
      width: 36px; height: 36px; border-radius: 50%;
      background: var(--bg-secondary); border: none;
      font-size: 22px; color: var(--text-secondary); cursor: pointer;
    }
    .aweni-wish-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-bottom: 16px;
    }
    @media (max-width: 380px) { .aweni-wish-grid { grid-template-columns: repeat(2, 1fr); } }
    .aweni-wish-tile {
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      padding: 14px 6px;
      border-radius: 14px;
      background: var(--bg-secondary);
      border: 2px solid transparent;
      color: var(--text-primary);
      cursor: pointer;
      min-height: 90px;
    }
    .aweni-wish-tile.selected { border-color: var(--accent); }
    .aweni-wish-tile-emoji { font-size: 28px; line-height: 1; }
    .aweni-wish-tile-label { font-size: 11px; font-weight: 600; text-align: center; line-height: 1.2; }
    .aweni-wish-card-preview {
      border-radius: 18px;
      padding: 18px;
      color: #fff;
      font-size: 14px;
      line-height: 1.6;
      white-space: pre-wrap;
      margin-bottom: 12px;
      min-height: 120px;
    }
    .aweni-wish-form-row {
      display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px;
    }
    .aweni-wish-form-row label { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
    .aweni-wish-form-row input, .aweni-wish-form-row textarea {
      padding: 10px 12px; border-radius: 10px;
      background: var(--bg-secondary); color: var(--text-primary);
      border: 1px solid var(--border);
      font-size: 14px; min-height: 40px;
      font-family: inherit;
    }
    .aweni-wish-form-row textarea { min-height: 110px; resize: vertical; }
    .aweni-wish-actions { display: flex; gap: 8px; margin-top: 14px; }
    .aweni-wish-action {
      flex: 1; padding: 12px 14px; border-radius: 12px;
      font-size: 14px; font-weight: 700;
      border: none; cursor: pointer; min-height: 48px;
    }
    .aweni-wish-action.primary { background: #25D366; color: #fff; }
    .aweni-wish-action.secondary { background: var(--bg-secondary); color: var(--text-primary); }
  `;
  document.head.appendChild(style);
}

function fillTemplate(tpl: string, name: string, sender: string): string {
  return tpl.replace(/\{\{name\}\}/g, name || '').replace(/\{\{sender\}\}/g, sender || '');
}

export function showWishingComposer(opts: ComposerOptions): void {
  injectStyles();
  const t = opts.ctx.i18n.t.bind(opts.ctx.i18n);
  const locale = opts.ctx.i18n.locale || 'en';
  const isRtl = opts.ctx.i18n.dir === 'rtl';
  const senderName = opts.ctx.auth.user?.name?.split(' ')[0] || '';

  let selectedId: WishingOccasionId = opts.preselect || 'jumuah';
  let recipientName = '';
  let messageText = '';

  const overlay = document.createElement('div');
  overlay.className = 'aweni-wish-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

  const dismiss = () => overlay.remove();

  const refresh = () => {
    const occ = getOccasionById(selectedId)!;
    const { template } = localized(occ, locale);
    if (!messageText) messageText = template;
    const sheet = overlay.querySelector('.aweni-wish-sheet') as HTMLElement;
    sheet.innerHTML = `
      <div class="aweni-wish-header">
        <span style="font-size:24px;" aria-hidden="true">${occ.emoji}</span>
        <span class="aweni-wish-title">${t('wish_title') || 'Send a wish'}</span>
        <button type="button" class="aweni-wish-close" aria-label="${t('common_close') || 'Close'}">×</button>
      </div>

      <div class="aweni-wish-grid" role="radiogroup" aria-label="${t('wish_pick_occasion') || 'Pick occasion'}">
        ${OCCASIONS.map((o) => `
          <button type="button" class="aweni-wish-tile ${o.id === selectedId ? 'selected' : ''}"
                  data-occ="${o.id}" role="radio" aria-checked="${o.id === selectedId}">
            <span class="aweni-wish-tile-emoji" aria-hidden="true">${o.emoji}</span>
            <span class="aweni-wish-tile-label">${localized(o, locale).label}</span>
          </button>
        `).join('')}
      </div>

      <div class="aweni-wish-card-preview" style="background:${occ.gradient};">
        ${(messageText || fillTemplate(template, recipientName || '___', senderName || '___'))
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}
      </div>

      <div class="aweni-wish-form-row">
        <label for="aweni-wish-recipient">${t('wish_recipient') || 'Recipient name'}</label>
        <input id="aweni-wish-recipient" type="text" placeholder="${t('wish_recipient_placeholder') || 'e.g., Aïcha'}" value="${recipientName.replace(/"/g, '&quot;')}" />
      </div>
      <div class="aweni-wish-form-row">
        <label for="aweni-wish-message">${t('wish_message') || 'Message'}</label>
        <textarea id="aweni-wish-message" dir="${isRtl ? 'rtl' : 'auto'}">${(messageText || fillTemplate(template, recipientName || '', senderName)).replace(/</g, '&lt;')}</textarea>
      </div>

      <div class="aweni-wish-actions">
        <button type="button" class="aweni-wish-action primary" data-action="whatsapp">
          ${t('wish_send_whatsapp') || 'Send via WhatsApp'}
        </button>
        <button type="button" class="aweni-wish-action secondary" data-action="share">
          ${t('wish_share_other') || 'Share…'}
        </button>
      </div>
    `;

    sheet.querySelector('.aweni-wish-close')?.addEventListener('click', dismiss);
    sheet.querySelectorAll('[data-occ]').forEach((b) => b.addEventListener('click', () => {
      selectedId = (b as HTMLElement).dataset.occ as WishingOccasionId;
      messageText = ''; // re-pick template
      refresh();
    }));
    const recipInput = sheet.querySelector('#aweni-wish-recipient') as HTMLInputElement;
    const msgInput = sheet.querySelector('#aweni-wish-message') as HTMLTextAreaElement;
    recipInput.addEventListener('input', () => {
      recipientName = recipInput.value;
      // Auto-update message if user hasn't manually edited it yet
      const occ2 = getOccasionById(selectedId)!;
      const { template: tpl2 } = localized(occ2, locale);
      const expected = fillTemplate(tpl2, '', senderName);
      const current = msgInput.value;
      // Heuristic: if current matches the previous template (with old name),
      // update with the new name; otherwise keep user's edits.
      if (current.includes('{{name}}') || current === fillTemplate(tpl2, recipientName === recipInput.value ? '' : recipientName, senderName) || current === expected) {
        const newMsg = fillTemplate(tpl2, recipInput.value, senderName);
        msgInput.value = newMsg;
        messageText = newMsg;
        // Update preview
        const prev = sheet.querySelector('.aweni-wish-card-preview') as HTMLElement;
        if (prev) prev.textContent = newMsg;
      }
    });
    msgInput.addEventListener('input', () => {
      messageText = msgInput.value;
      const prev = sheet.querySelector('.aweni-wish-card-preview') as HTMLElement;
      if (prev) prev.textContent = msgInput.value;
    });

    sheet.querySelector('[data-action="whatsapp"]')?.addEventListener('click', () => {
      const final = (msgInput.value || fillTemplate(template, recipientName, senderName)).trim();
      // wa.me opens WhatsApp with text pre-filled — user picks the recipient
      // chat manually inside WhatsApp. Privacy-clean: no contact API access.
      const url = `https://wa.me/?text=${encodeURIComponent(final)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    });
    sheet.querySelector('[data-action="share"]')?.addEventListener('click', async () => {
      const final = (msgInput.value || fillTemplate(template, recipientName, senderName)).trim();
      // Use native share sheet if available (iOS, Android, modern desktop)
      const nav = navigator as Navigator & {
        share?: (d: ShareData) => Promise<void>;
        clipboard?: { writeText(s: string): Promise<void> };
      };
      if (typeof nav.share === 'function') {
        try {
          await nav.share({ title: localized(getOccasionById(selectedId)!, locale).label, text: final });
        } catch { /* user cancelled */ }
      } else if (nav.clipboard) {
        // Fallback: copy to clipboard
        try {
          await nav.clipboard.writeText(final);
          alert(t('wish_copied') || 'Message copied to clipboard');
        } catch { /* */ }
      }
    });
  };

  overlay.innerHTML = `<div class="aweni-wish-sheet" role="document"></div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });
  document.body.appendChild(overlay);
  refresh();
}
